import {AxiosInstance} from "axios";
import {ApiError, ErrorType} from "@/api/api";
import {panelBackUrl} from "@/config";

export interface Template {
    template: string;
    images: number;
    terminals: number;
    children: Array<Template>;
}

export interface Distr {
    distributions: Array<string>;
    distributionsType: string;
    defaultDistribution: string;
}

export interface Session {
    sessionId: string;
    state: string;
    activationCode: number;
    hallId: number;
    comment: string;
    autoActivated: boolean;
    createdAt: string;
    expiresAt: string;
    updatedAt: string;
    mac: string
}

export interface SessionResp {
    total: number;
    offset: number;
    limit: number;
    sessions: Session[];
}

export interface SessionSettingsResp {
    autoActivate: {
        enabled: boolean;
        expiredAt: string;
    };
}

export interface TemplateCollection {
    default: string;
    templates: Template[];
}

export async function getDistrs(http:AxiosInstance): Promise<Distr[] | ApiError> {
    return await http
        .post(`${panelBackUrl}/v1/proxy/api`, {
            url: `/v2/distributions/types`,
            method: "GET",
            payload: {},
            headers: {},
        })
        .then(async (res: any): Promise<Distr[] | ApiError> => {
            console.log("Response from proxy obtained (get distrs)");
            switch (res.data.statusCode) {
                case 200:
                    return res.data.payload.loadingDistrTypes.map((distr: any) => {
                        return {
                            distributions: distr.distributions as string[],
                            distributionsType: distr.distributionsType as string,
                            defaultDistribution: distr.defaultDistribution as string,
                        }
                })
                case 403:
                    return {
                        errType: ErrorType.Forbidden,
                    }
                default:
                    console.log("Response proxy error (get distrs)", res.data);
                    return {
                        errType: ErrorType.Other,
                        data: res.data.payload
                    }

            }
        })
        .catch((error: any) => {
            console.log("Error to get distrs", error);
            if (error.response.status === 403) {
                return {
                    errType: ErrorType.Forbidden,
                }
            }
            return {
                errType: ErrorType.Other,
                data: error.data
            }
        });
}

export async function getSessions(
    http:AxiosInstance,
    state: string | null | undefined,
    hallId: number| null | undefined,
    limit: number,
    offset: number
): Promise<SessionResp | ApiError> {
    const queryParams: Record<string, string | number | null | undefined> = {
        limit: limit,
        offset: offset,
        state: state,
        hallId: hallId
    };

    const cleanParams = new URLSearchParams();
    Object.entries(queryParams).forEach(([key, value]) => {
        if (value) {
            cleanParams.append(key, String(value));
        }
    });

    return await http
        .post(`${panelBackUrl}/v1/proxy/api`, {
            url: `/v2/installer/sessions?${cleanParams.toString()}`,
            method: "GET",
            payload: {},
            headers: {},
        })
        .then(async (res: any): Promise<SessionResp | ApiError> => {
            console.info("Response from proxy obtained (get sessions)");
            switch (res.data.statusCode) {
                case 200:
                        return {
                            total: res.data.payload.total,
                            limit: res.data.payload.limit,
                            offset: res.data.payload.offset,
                            sessions: res.data.payload.sessions.map((s: any) => {
                                return {
                                    sessionId: s.sessionId,
                                    state: s.state,
                                    activationCode: s.activationCode,
                                    hallId: s.hallId,
                                    comment: s.comment,
                                    autoActivated: s.autoActivated,
                                    createdAt: s.createdAt,
                                    expiresAt: s.expiresAt,
                                    updatedAt: s.updatedAt,
                                    mac: s.mac,
                                }
                            })
                        }
                case 403:
                    return {
                        errType: ErrorType.Forbidden,
                    }
                default:
                    console.error("Response proxy error (get sessions)", res.data);
                    return {
                        errType: ErrorType.Other,
                        data: res.data.payload
                    }

            }
        })
        .catch((error: any) => {
            console.error("Error to get sessions", error);
            if (error.response.status === 403) {
                return {
                    errType: ErrorType.Forbidden,
                }
            }
            return {
                errType: ErrorType.Other,
                data: error.data
            }
        });
}

export async function activateSessionCode(
    http:AxiosInstance,
    code: number,
    hallId: number,
    comment: string,

): Promise<null | ApiError> {

    return await http
        .post(`${panelBackUrl}/v1/proxy/api`, {
            url: `/v2/installer/sessions/activate`,
            method: "POST",
            payload: {
                activationCode: code,
                hallId: hallId,
                comment: comment,
            },
            headers: {},
        })
        .then(async (res: any): Promise<null | ApiError> => {
            console.info("Response from proxy obtained (post active session code)");
            switch (res.data.statusCode) {
                case 200:
                    return null
                case 403:
                    return {
                        errType: ErrorType.Forbidden,
                    }
                default:
                    console.error("Response proxy error (post active session code)", res.data);
                    return {
                        errType: ErrorType.Other,
                        data: res.data.payload
                    }

            }
        })
        .catch((error: any) => {
            console.error("post active session code", error);
            if (error.response.status === 403) {
                return {
                    errType: ErrorType.Forbidden,
                }
            }
            return {
                errType: ErrorType.Other,
                data: error.data
            }
        });
}

export async function saveSessionGlobalSettings(
    http:AxiosInstance,
    name: string,
    action: string,

): Promise<any | ApiError> {

    return await http
        .post(`${panelBackUrl}/v1/proxy/api`, {
            url: `/v2/installer/settings/${name}/${action}`,
            method: "POST",
            payload: {},
            headers: {},
        })
        .then(async (res: any): Promise<any | ApiError> => {
            console.info("Response from proxy obtained (post save session settings)");
            switch (res.data.statusCode) {
                case 200:
                    return res.data.payload
                case 403:
                    return {
                        errType: ErrorType.Forbidden,
                    }
                default:
                    console.error("Response proxy error (post save session settings)", res.data);
                    return {
                        errType: ErrorType.Other,
                        data: res.data.payload
                    }

            }
        })
        .catch((error: any) => {
            console.error("post save session settings", error);
            if (error.response.status === 403) {
                return {
                    errType: ErrorType.Forbidden,
                }
            }
            return {
                errType: ErrorType.Other,
                data: error.data
            }
        });
}


export async function GetSessionGlobalSettings(
    http:AxiosInstance
): Promise<SessionSettingsResp | ApiError> {
    return await http
        .post(`${panelBackUrl}/v1/proxy/api`, {
            url: `/v2/installer/settings`,
            method: "GET",
            payload: {},
            headers: {},
        })
        .then(async (res: any): Promise<SessionSettingsResp | ApiError> => {
            console.info("Response from proxy obtained (post save session settings)");
            switch (res.data.statusCode) {
                case 200:
                    return {
                        autoActivate: {
                            enabled: res.data.payload.autoActivation.enabled,
                            expiredAt: res.data.payload.autoActivation.exiredAt,
                    } }
                case 403:
                    return {
                        errType: ErrorType.Forbidden,
                    }
                default:
                    console.error("Response proxy error (post save session settings)", res.data);
                    return {
                        errType: ErrorType.Other,
                        data: res.data.payload
                    }

            }
        })
        .catch((error: any) => {
            console.error("post save session settings", error);
            if (error.response.status === 403) {
                return {
                    errType: ErrorType.Forbidden,
                }
            }
            return {
                errType: ErrorType.Other,
                data: error.data
            }
        });
}



export async function resetDistrOnTerminals(http:AxiosInstance, terminals: number[], distrType: string): Promise<null | ApiError> {
    return await http
        .post(`${panelBackUrl}/v1/proxy/api`, {
            url: `/v2/terminals/distribution?distrType=${distrType}&terminalIds=${terminals.join(',')}`,
            method: "DELETE",
            payload: {},
            headers: {},
        })
        .then(async (res: any): Promise<null | ApiError> => {
            console.info("Response from proxy obtained (reset distr on terminals)");
            switch (res.data.statusCode) {
                case 200:
                    return null
                case 403:
                    return {
                        errType: ErrorType.Forbidden,
                    }
                default:
                    console.info("Response proxy error (reset distr on terminals)", res.data);
                    return {
                        errType: ErrorType.Other,
                        data: res.data.payload
                    }

            }
        })
        .catch((error: any) => {
            console.log("Error resetting distr in terminals", error);
            if (error.response.status === 403) {
                return {
                    errType: ErrorType.Forbidden,
                }
            }
            return {
                errType: ErrorType.Other,
                data: error.data
            }
        });
}

export async function setDistrsToTerminals(http:AxiosInstance, terminals: number[], distr: string): Promise<null | ApiError> {
    return await http
        .post(`${panelBackUrl}/v1/proxy/api`, {
            url: `/v2/terminals/distribution`,
            method: "POST",
            payload: {
                "terminalIds": terminals,
                "distrName": distr
            },
            headers: {},
        })
        .then(async (res: any): Promise<null | ApiError> => {
            console.log("Response from proxy obtained (set distr to terminals)");
            switch (res.data.statusCode) {
                case 200:
                    return null
                case 403:
                    return {
                        errType: ErrorType.Forbidden,
                    }
                default:
                    console.log("Response proxy error (set distr to terminals)", res.data);
                    return {
                        errType: ErrorType.Other,
                        data: res.data.payload
                    }

            }
        })
        .catch((error: any) => {
            console.log("Error setting template to terminals", error);
            if (error.response.status === 403) {
                return {
                    errType: ErrorType.Forbidden,
                }
            }
            return {
                errType: ErrorType.Other,
                data: error.data
            }
        });
}

export async function getTemplates(http:AxiosInstance): Promise<TemplateCollection | ApiError> {
    return await http
        .post(`${panelBackUrl}/v1/proxy/api`, {
            url: `/v3/templates`,
            method: "GET",
            payload: {},
            headers: {},
        })
        .then(async (res: any): Promise<TemplateCollection | ApiError> => {
            console.log("Response from proxy obtained (get templates)");
            switch (res.data.statusCode) {
                case 200:
                    return {
                        templates: res.data.payload.templates as any,
                        default: res.data.payload.default
                    }
                case 403:
                    return {
                        errType: ErrorType.Forbidden,
                    }
                default:
                    console.log("Response proxy error (get templates)", res.data);
                    return {
                        errType: ErrorType.Other,
                        data: res.data.payload
                    }

            }
        })
        .catch((error: any) => {
            console.log("Error to get templates", error);
            if (error.response.status === 403) {
                return {
                    errType: ErrorType.Forbidden,
                }
            }
            return {
                errType: ErrorType.Other,
                data: error.data
            }
        });
}

export async function setTemplateToTerminals(http:AxiosInstance, terminals: number[], template: string): Promise<null | ApiError| number> {
    return await http
        .post(`${panelBackUrl}/v1/proxy/api`, {
            url: `/v2/terminals/template`,
            method: "POST",
            payload: {
                terminalIds: terminals,
                template: template
            },
            headers: {},
        })
        .then(async (res: any): Promise<null | ApiError | number> => {
            console.log("Response from proxy obtained (set template to terminals)");
            switch (res.data.statusCode) {
                case 200: {
                    // const errors: number = res.data.payload.results.filter((el: any): boolean => {
                    //     return el.status === "ERROR"
                    // }).length
                    // if (errors > 0) {
                    //     return errors
                    // }
                    return null
                }

                case 403:
                    return {
                        errType: ErrorType.Forbidden,
                    }
                default:
                    console.log("Response proxy error (set template to terminals)", res.data);
                    return {
                        errType: ErrorType.Other,
                        data: res.data.payload
                    }

            }
        })
        .catch((error: any) => {
            console.log("Error setting template to terminals", error);
            if (error.response.status === 403) {
                return {
                    errType: ErrorType.Forbidden,
                }
            }
            return {
                errType: ErrorType.Other,
                data: error.data
            }
        });
}